import React from 'react'
import { Badge } from 'reactstrap'

const badgeStyle = { fontSize: '0.65rem', marginRight: 4, marginTop: 2 }

export const FERIADO_COLOR = '#50a5f1'
export const FERIAS_COLOR = '#556ee6'
export const ABONO_COLOR = '#34c38f'

export function DayFlagBadges({ day }) {
  if (!day) return null
  return (
    <span className="d-inline-flex flex-wrap align-items-center">
      {day.alerta_conflito && (
        <Badge color="danger" pill style={badgeStyle}>Conflito</Badge>
      )}
      {!day.alerta_conflito && day.alerta_bloco_longo && (
        <Badge color="warning" pill style={badgeStyle}>Bloco &gt; 6h</Badge>
      )}
      {!day.alerta_conflito && !day.alerta_bloco_longo && day.alerta_limite_diario && (
        <Badge color="warning" pill style={badgeStyle}>&gt; 10h no dia</Badge>
      )}
      {(Number(day.qtd_feriados) > 0 || day.tem_feriado) && (
        <Badge color="info" pill style={{ ...badgeStyle, backgroundColor: FERIADO_COLOR }}>Feriado</Badge>
      )}
      {Number(day.qtd_ferias) > 0 && (
        <Badge color="primary" pill style={badgeStyle}>Férias</Badge>
      )}
      {Number(day.qtd_creditos) > 0 && (
        <Badge color="secondary" pill style={badgeStyle}>Abono</Badge>
      )}
    </span>
  )
}

export function PontoFlagBadges({ ponto }) {
  if (!ponto) return null
  const isFeriado = ponto.is_feriado
    || /FERIADO/i.test(String(ponto.ponto_justif || ''))
    || (ponto.atividades || []).some(a => a.is_feriado)
  const isFerias = !isFeriado && (
    ponto.is_ferias
    || /FERIAS/i.test(String(ponto.ponto_justif || ''))
    || (ponto.atividades || []).some(a => a.is_ferias)
  )
  const isAbono = !isFeriado && !isFerias && (
    ponto.is_abono
    || (ponto.atividades || []).some(a => a.is_abono)
  )
  if (!isFeriado && !isFerias && !isAbono) return null
  return (
    <span className="d-inline-flex flex-wrap align-items-center ml-1">
      {isFeriado && (
        <Badge color="info" pill style={{ ...badgeStyle, backgroundColor: FERIADO_COLOR }}>Feriado</Badge>
      )}
      {isFerias && (
        <Badge color="primary" pill style={badgeStyle}>Férias</Badge>
      )}
      {isAbono && (
        <Badge color="secondary" pill style={badgeStyle}>Abono</Badge>
      )}
    </span>
  )
}

export function ActivityFlagBadges({ activity }) {
  if (!activity) return null
  return (
    <span className="d-inline-flex flex-wrap align-items-center">
      {activity.is_feriado && (
        <Badge color="info" pill style={{ ...badgeStyle, backgroundColor: FERIADO_COLOR }}>Feriado</Badge>
      )}
      {activity.is_ferias && (
        <Badge color="primary" pill style={badgeStyle}>Férias</Badge>
      )}
      {activity.is_abono && !activity.is_ferias && !activity.is_feriado && (
        <Badge color="secondary" pill style={badgeStyle}>Abono</Badge>
      )}
      {activity.is_retroativo && (
        <Badge color="dark" pill style={badgeStyle}>Retroativo</Badge>
      )}
      {Number(activity.pontoAtiv_aprov) === 0 && (
        <Badge color="warning" pill style={badgeStyle}>Pendente</Badge>
      )}
      {Number(activity.pontoAtiv_aprov) === 2 && (
        <Badge color="danger" pill style={badgeStyle}>Recusada</Badge>
      )}
    </span>
  )
}

export function isFeriadoPonto(ponto) {
  if (!ponto) return false
  if (ponto.is_feriado) return true
  if (/FERIADO/i.test(String(ponto.ponto_justif || ''))) return true
  return (ponto.atividades || []).some(a => a.is_feriado)
}

export function chartBarColor(day) {
  if (!day) return '#5664d2'
  if (day.alerta_conflito) return '#f46a6a'
  if (day.alerta_bloco_longo || day.alerta_limite_diario) return '#f1b44c'
  if (Number(day.qtd_feriados) > 0 || day.tem_feriado) return FERIADO_COLOR
  if (Number(day.qtd_ferias) > 0 || Number(day.qtd_creditos) > 0) return ABONO_COLOR
  return '#5664d2'
}

export function hoursFromSeconds(seconds) {
  return Number(((seconds || 0) / 3600).toFixed(2))
}

export function fmtOrDash(value) {
  if (value === null || value === undefined || value === '') return '—'
  return value
}

/**
 * Célula de horas com badge.
 * kind=feriado → badge Feriado; kind=abono → badge Férias se feriasSeconds>0,
 * senão Abono (crédito genérico). Não inventa horas.
 */
export function MonthHoursCell({ value, seconds, kind, feriasSeconds = 0 }) {
  const secs = Number(seconds || 0)
  const feriasSecs = Number(feriasSeconds || 0)
  const has = secs > 0
  if (!has) return <span className="text-muted">—</span>
  const onlyFerias = kind === 'abono' && feriasSecs > 0 && feriasSecs >= secs
  const mixedFerias = kind === 'abono' && feriasSecs > 0 && feriasSecs < secs
  return (
    <span className="d-inline-flex align-items-center flex-wrap">
      <span style={kind === 'feriado' ? { fontWeight: 700, color: '#0c5460' } : undefined}>
        {fmtOrDash(value)}
      </span>
      {kind === 'feriado' && (
        <Badge color="info" pill className="ml-1" style={{ ...badgeStyle, backgroundColor: FERIADO_COLOR }}>
          Feriado
        </Badge>
      )}
      {kind === 'abono' && onlyFerias && (
        <Badge color="primary" pill className="ml-1" style={badgeStyle}>Férias</Badge>
      )}
      {kind === 'abono' && mixedFerias && (
        <>
          <Badge color="primary" pill className="ml-1" style={badgeStyle}>Férias</Badge>
          <Badge color="secondary" pill className="ml-1" style={badgeStyle}>Abono</Badge>
        </>
      )}
      {kind === 'abono' && feriasSecs <= 0 && (
        <Badge color="secondary" pill className="ml-1" style={badgeStyle}>Abono</Badge>
      )}
    </span>
  )
}
